import type { RoundingStrategyDataModel } from '@/entities/pricing-campaign'
import type { RoundingRuleValueModel } from '@/shared/generated'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'
import type { JSONSchemaType } from 'ajv'
import type { ChangeEvent, MouseEvent } from 'react'

import React, { useMemo, useRef } from 'react'
import { pipe, sortBy } from 'lodash/fp'
import {
	AlertTriangleIcon,
	ChevronRightIcon,
	CircleHelpIcon,
	PlusIcon,
	Trash2Icon,
} from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import HookSchemaForm from '@/shared/components/HookSchemaForm'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Item, ItemContent, ItemTitle } from '@/shared/components/ui/item'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { numericWithTwoDecimals } from '@/shared/tools/constants'
import { translateSchemaKeys } from '@/shared/tools/json-schema-utils'
import { asNumber } from '@/shared/tools/utils'

const update = (
	index: number,
	updater: (rule: Partial<RoundingRuleValueModel>) => RoundingRuleValueModel,
	source: Partial<RoundingRuleValueModel>[]
) => source.map((r, _index) => (_index === index ? updater(r) : r))

const updateRule =
	(data: RoundingRuleValueModel) =>
	(rule: Partial<RoundingRuleValueModel>): RoundingRuleValueModel => ({
		...rule,
		...data,
	})

const addRule = (rules: RoundingRuleValueModel[]) => {
	const data = [...rules]
	const prevStep = data[data.length - 1]

	return [
		...data,
		{
			min_price: +(prevStep.min_price || 0) + 500,
			strategy: 'ignore',
		},
	]
}
type RuleSettingsProps = {
	errorSchema?: ErrorSchema
	namePrefix: string
	strategies: RoundingStrategyDataModel[]
	rule: RoundingRuleValueModel
	activeIndex: number
}
function RuleSettings({
	namePrefix,
	strategies,
	rule,
	activeIndex,
	errorSchema,
}: RuleSettingsProps) {
	const { t } = useTranslation()

	const { setValue } = useFormContext()
	const roundingPath = `${namePrefix}.${activeIndex}`
	const roundingStrategyPath = `${roundingPath}.strategy`
	const roundingStrategyParamsPath = `${roundingPath}.params`
	const strategy = useMemo(
		() => strategies.find((item) => item.value === rule?.strategy),
		[rule, strategies]
	)

	return (
		<div className='flex w-full flex-col gap-3'>
			<FormField
				name={roundingStrategyPath}
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							{t('app.strategy', { defaultValue: 'Strategy' })}
							<Tooltip>
								<TooltipTrigger asChild>
									<CircleHelpIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent>
									{t('how_to_use_rounding_strategy_tooltip')}
								</TooltipContent>
							</Tooltip>
						</FormLabel>
						<Select
							value={field.value}
							onValueChange={(value) => {
								field.onChange(value)
								setValue(roundingStrategyParamsPath, {})
							}}
						>
							<FormControl>
								<SelectTrigger className='w-72'>
									<SelectValue />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								<SelectGroup>
									{strategies.map((strategy) => (
										<SelectItem key={strategy.value} value={strategy.value}>
											{strategy.label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</FormItem>
				)}
			/>
			{strategy?.schema?.description && (
				<Item size='xs' variant='muted'>
					<ItemContent>
						<ItemTitle>{t('strategy_description')}</ItemTitle>
						<AlertDescription>
							{t(strategy?.schema?.description, {
								defaultValue: strategy?.schema?.description,
							})}
						</AlertDescription>
					</ItemContent>
				</Item>
			)}
			<div className='grid gap-3'>
				{strategy?.schema && (
					<HookSchemaForm
						key={`${activeIndex}_${strategy?.value}_rounding_rule`}
						schema={translateSchemaKeys(strategy?.schema, t)}
						errorSchema={errorSchema?.params}
						name={roundingStrategyParamsPath}
						uiSchema={{}}
					/>
				)}
			</div>
		</div>
	)
}
type RoundingRulesManagerProps = {
	namePrefix: string
	rules: RoundingRuleValueModel[]
	onChange(value: RoundingRuleValueModel[]): void
	errorSchema?: ErrorSchema
	schema: JSONSchemaType<RoundingRuleValueModel[]>
}
export const RoundingRulesFields = (props: RoundingRulesManagerProps) => {
	const { t } = useTranslation()

	const { rules, onChange, errorSchema, schema, namePrefix } = props
	const addButtonRef = useRef<HTMLButtonElement>(null)
	const strategies: RoundingStrategyDataModel[] = (
		schema.items.oneOf || []
	).map((rule: JSONSchemaType<any>) => ({
		label: t(rule.properties.strategy.title, {
			defaultValue: rule.properties.strategy.title,
		}),
		value: rule.properties.strategy.const,
		schema: rule.properties.params as JSONSchemaType<
			typeof rule.properties.params
		>,
	}))
	const handleAddRule = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		ampli.settingsPricingCampaignsAddRoundingRule()
		onChange(addRule(rules))
	}
	const handleRuleDelete = (index: number) => {
		ampli.settingsPricingCampaignsDeleteRoundingRule()

		onChange(rules.filter((rule, i) => i !== index))
		// Focus the add button after deleting a rule
		addButtonRef.current?.focus()
	}
	const onChangeRuleRange =
		(index: number) => (event: ChangeEvent<HTMLInputElement>) => {
			const maxValue = asNumber(
				event.target.value.replace(numericWithTwoDecimals, '$1')
			) as number
			const newData = { min_price: maxValue < 0 ? 1 : maxValue }
			onChange(update(index, updateRule(newData), rules))
		}
	const handleBlur = () => pipe([sortBy('min_price'), onChange])(rules)

	return (
		<>
			<div className='mb-2 flex w-full flex-col divide-y overflow-hidden border-y'>
				{rules.map((rule, ruleIndex) => {
					const max = rules[ruleIndex + 1]
						? ((rules[ruleIndex + 1].min_price || 0) - 0.01).toFixed(2)
						: 'max'
					const activeStrategy = strategies.find(
						(item) => item.value === rule?.strategy
					)
					const error = errorSchema?.[ruleIndex]?.__errors?.[0]

					return (
						<Collapsible
							key={ruleIndex}
							className='relative flex w-full flex-col justify-center px-3 py-2 data-[state=open]:bg-secondary/30'
						>
							<div className='flex w-full items-center gap-2'>
								<CollapsibleTrigger asChild>
									<Button
										variant='ghost'
										size='icon-sm'
										className='tansition-all data-[state=open]:rotate-90'
									>
										<ChevronRightIcon />
									</Button>
								</CollapsibleTrigger>
								<div className='w-full'>
									<div className='flex items-center gap-2'>
										<div className='flex shrink-0 items-center text-sm font-medium'>
											{t('general_range')} {ruleIndex + 1}
										</div>
										<Input
											className='w-auto'
											name='min_price'
											inputMode='numeric'
											type='number'
											data-testid='pc-rounding-rule-list-item-min'
											value={rule.min_price}
											onBlur={handleBlur}
											disabled={ruleIndex === 0}
											invalid={!('' + rule.min_price).length || error}
											placeholder={t('empty')}
											onChange={onChangeRuleRange(ruleIndex)}
										/>
										<span>-</span>
										<Input
											className='w-auto'
											name='max_price'
											data-testid='pc-rounding-rule-list-item-max'
											value={max}
											disabled
											onChange={onChangeRuleRange(ruleIndex + 1)}
										/>
										<div className='mr-auto flex items-center justify-between gap-2 text-right text-sm text-muted-foreground'>
											{t('strategy', { defaultValue: 'Strategy' })}:{' '}
											{activeStrategy?.label}
										</div>
										{errorSchema?.[ruleIndex] && (
											<Alert variant='destructive'>
												<AlertTriangleIcon />
												<AlertDescription>
													{t('rounding.invalid.params', {
														defaultValue: 'Invliad strategy parameters',
													})}
												</AlertDescription>
											</Alert>
										)}
										{ruleIndex !== 0 ? (
											<Button
												variant='ghost'
												onClick={() => handleRuleDelete(ruleIndex)}
												data-testid='pc-rounding-rule-delete'
												tabIndex={0}
												size='icon'
											>
												<Trash2Icon />
											</Button>
										) : (
											<div className='size-8' />
										)}
									</div>
								</div>
							</div>
							<CollapsibleContent className='grid gap-2 py-2 pr-4 pl-8'>
								<Separator orientation='horizontal' />
								<RuleSettings
									namePrefix={namePrefix}
									rule={rule}
									strategies={strategies}
									activeIndex={ruleIndex!}
									errorSchema={errorSchema?.[ruleIndex]}
								/>
								{errorSchema?.[ruleIndex] && (
									<Alert variant='destructive'>
										<AlertTitle>{t('rounding.invalid.params')}</AlertTitle>
										<AlertDescription>
											{t('rounding.invalid.params.extended', {
												defaultValue:
													'Strategy parameters are invalid, please check them again and try to save',
											})}
										</AlertDescription>
									</Alert>
								)}
							</CollapsibleContent>
						</Collapsible>
					)
				})}
			</div>
			<Button
				data-testid='add-rounding-rule-button'
				ref={addButtonRef}
				variant='ghost'
				onClick={handleAddRule}
			>
				<PlusIcon />
				{t('rounding_add')}
			</Button>
		</>
	)
}
